import { NavButton } from 'Components/ClientApp/CombinedApp/Navigation/NavButton';
import React, { useMemo } from 'react';
import fr from 'facet-rest';
import useCollectionState from 'Hooks/useCollectionState';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { pick } from 'Helpers/Affordances';
import { buildClientLink, buildPageLink, navigateToPage } from 'Views/Page/Content/Templates';
import { clientManagedAccountUri } from 'Consts/clientUris';
import { goToAccountOpeningFlow } from 'Helpers/Navigation';
import { handleApexAccountOnClick } from 'Components/UI/Accounts/AccountManagement/AccountManagementTable';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { useHistory } from 'react-router';
import { currency } from 'Helpers/Formats';
import {
  FDBodyText,
  FDBox,
  FDImage,
  FDLozenge,
  FDSecondaryActionButton,
  FDStack,
  FDTitleText,
} from '@facet-wealth/design';
import useMobileLayout from 'Hooks/useMobileLayout';
import useUserContext from 'Context/UserContext';
import { clientPaths } from 'Consts/Strings';
import { useNavButtonsContext } from 'Components/ClientApp/CombinedApp/Navigation/NavButtonsContext';
import { getApexAccountCreationStatusInfoColor, normalizeApexSyncStatus } from 'Consts/Apex';
import categories from 'EventTracking/consts/categories';
import eventTypes from 'EventTracking/consts/eventTypes';
import eventSubTypes from 'EventTracking/consts/eventSubTypes';
import { useRefreshAccountStatusChanged } from 'Hooks/useRefreshEvent';

export const InvestmentsNavButton = () => {
  const { householdResource } = useSimpleHousehold();
  const { userResource } = useUserContext();
  const { collection: managedAccounts, refreshCollection: refreshManagedAccounts } = useCollectionState({ link: householdResource?.relationship(fr.relationships.managedAccounts.name)?.link('related') });
  const { collection: submittedButNotOpenAccounts, refreshCollection: refreshSubmittedButNotOpenAccounts } = useCollectionState({ link: householdResource?.relationship(fr.relationships.unopenedAccountCreationRequests.name)?.link('related') });
  const { collection: unsubmittedAccounts, refreshCollection: refreshUnsubmittedAccounts } = useCollectionState({ link: householdResource?.relationship(fr.relationships.unopenedApexAccountDetails.name)?.link('related') });
  const isMobile = useMobileLayout();
  const btnValue = 'investments';

  const { logEvent, logStandardizedEvent } = useEventTrackingLogEvent();

  useRefreshAccountStatusChanged(() => {
    refreshManagedAccounts();
    refreshSubmittedButNotOpenAccounts();
    refreshUnsubmittedAccounts();
  }, [refreshManagedAccounts, refreshSubmittedButNotOpenAccounts, refreshUnsubmittedAccounts]);

  const history = useHistory();

  const accounts = useMemo(() => {
    const accLinks = [];

    if (householdResource.relationship(fr.relationships.managedAccountDocumentsPage.name)?.link('related')?.url()) {
      accLinks.push({
        key: 'managed-account-docs-page',
        primaryText: 'Statements & Tax Docs',
        secondaryText: 'A single location for your Facet account documents.',
        avatar: '',
        lozenge: <FDLozenge color={'info'} label={'New'} />,
        'data-test': 'investment-dropdown-sublink-managed-account-managed-account-docs-page',
        onClick: () => {
          logStandardizedEvent(categories.managedAccount, eventTypes.click, eventSubTypes.navBarItem, {
            eventProperties: {
              accountName: 'managed-account-docs-page',
            },
          });
          navigateToPage(householdResource.relationship(fr.relationships.managedAccountDocumentsPage.name)?.link('related')?.url(), false);
        },
      });
    }

    if (submittedButNotOpenAccounts) {
      submittedButNotOpenAccounts.resources().list().forEach((accountDetails, i) => {
        const statusInfo = accountDetails.attr('statusInfo');
        const color = getApexAccountCreationStatusInfoColor(normalizeApexSyncStatus(statusInfo));
        accLinks.push({
          key: `${accountDetails.attr('accountType')}-${accountDetails.type()}-${accountDetails.id()}`,
          primaryText: accountDetails.attr('accountType'),
          onClick: () => {
            logStandardizedEvent(categories.managedAccount, eventTypes.pendingAccountClick, eventSubTypes.navBarItem, {
              eventProperties: {
                accountName: accountDetails.attr('accountType'),
              },
            });
            handleApexAccountOnClick(logEvent, accountDetails, householdResource, history, false, false);
          },
          'data-test': `investment-dropdown-sublink-unopened-account-${i}`,
          lozenge: <FDLozenge color={color} label={statusInfo} />,
        });
      });
    }
    if (unsubmittedAccounts) {
      unsubmittedAccounts.resources().list().forEach((accountDetails, i) => {
        accLinks.push({
          key: `${accountDetails.attr('name')}-${accountDetails.type()}-${accountDetails.id()}`,
          primaryText: accountDetails.attr('name'),
          onClick: () => {
            logStandardizedEvent(categories.managedAccount, eventTypes.openAccount, eventSubTypes.navBarItem, {
              eventProperties: {
                accountName: accountDetails.attr('name'),
              },
            });

            goToAccountOpeningFlow(accountDetails);
          },
          'data-test': `investment-dropdown-sublink-unsubmitted-account-${i}`,
          lozenge: <FDLozenge color="success" label="Ready to Open" />,
        });
      });
    }
    if (managedAccounts) {
      managedAccounts.resources().list().forEach((acc, i) => {
        const managedAccountLink = pick(acc, {
          [fr.relationships.managedAccountPage.name]: () => buildClientLink(acc.relationship(fr.relationships.managedAccountPage.name).link('related').url()),
          [fr.relationships.accountOpeningWizard.name]: () => buildClientLink(acc.link('self'), { link: acc.link('self').url() }),
        },
        () => clientManagedAccountUri(householdResource.id(), acc.link('self').url(), null))();

        accLinks.push({
          key: `${acc.attr('nickName')}-${acc.type()}-${acc.id()}`,
          primaryText: `${acc.attr('nickName')} - ${currency(acc.attr('balance'), true)}`,
          to: managedAccountLink,
          'data-test': `investment-dropdown-sublink-managed-account-${i}`,
          onClick: () => {
            logStandardizedEvent(categories.managedAccount, eventTypes.click, eventSubTypes.navBarItem, {
              eventProperties: {
                accountName: acc.attr('nickName'),
              },
            });
          },
        });
      });
    }

    householdResource.withRelationship(
      fr.relationships.nmarsPage.name,
      (rel) => {
        accLinks.push({
          key: 'nmars',
          primaryText: rel.meta('prompt'),
          to: buildPageLink(rel.link('related'), clientPaths.page),
          onClick: () => {
            logStandardizedEvent(categories.foundations, eventTypes.click, eventSubTypes.navBarItem, {
              eventProperties: {
                navBarItem: 'NMARS Page',
              },
            });
          },
        });
      },
      () => null,
    );

    return accLinks;
  }, [managedAccounts, submittedButNotOpenAccounts, unsubmittedAccounts, history, logEvent]);

  const { onNavClick } = useNavButtonsContext(btnValue);
  const messageMyPlannerBtn = userResource.withRelationship(
    fr.relationships.messagesPage.name,
    () => (
      <FDSecondaryActionButton
        size="sm"
        label="Message my Planner"
        onClick={() => {
          history.push(buildPageLink(userResource.relationship(fr.relationships.messagesPage.name).link('related'), clientPaths.messagesPage));
          onNavClick();
        }}
      />
    ),
    () => null,
  );

  return (
    <NavButton
      value={btnValue}
      label={'Investments'}
      isDropdown
      dropdownItems={accounts}
      dropdownFallback={(
        <FDBox
          padding={2}
          maxWidth={
            isMobile
              ? undefined
              : '562px' // Any larger than 570 and a 960px breakpoint will cause issues. Using 562 because it's 8 less
          }
        >
          <FDStack spacing={2}>
            <FDStack direction="column" spacing={1}>
              <FDTitleText value="If you had investments managed by Facet, you'd see them here." size="lg" />
              <FDBodyText value="One big way we help you achieve your goals is through investing. That's why it's included in your membership at no extra cost. Message your planner anytime about setting up your investments with Facet. (As a reminder, we never take commissions or a cut of your investments.)" size="sm" />
              <FDStack spacing={2} wrap>
                {messageMyPlannerBtn}
                <FDSecondaryActionButton
                  size="sm"
                  label="Learn More"
                  href="https://facet.com/investwithfacet/"
                />
              </FDStack>
            </FDStack>
            {!isMobile && <FDImage
              src="https://static.facetwealth.com/app/image/investments/invest-header.png"
              alt="People happy to be achieving their goals"
              width="200px"
            />}
          </FDStack>
        </FDBox>
      )}
    />
  );
};
